import { useInfiniteQuery } from "@tanstack/react-query";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { userConversationMessagesInfiniteOptions } from "@/hooks/use-user-conversations";
import type {
  ConversationMessage,
  ConversationSessionItem,
} from "@/types/user-conversation";

interface Props {
  session: ConversationSessionItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function roleLabel(role: string): string {
  if (role === "assistant") return "AI";
  if (role === "user") return "유저";
  return "시스템";
}

function MessageBubble({ message }: { message: ConversationMessage }) {
  const isAssistant = message.role === "assistant";
  return (
    <div
      className={[
        "rounded-md p-3 text-sm whitespace-pre-wrap break-words",
        isAssistant ? "bg-muted/50" : "bg-background border",
      ].join(" ")}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-xs font-medium text-muted-foreground">
          {roleLabel(message.role)}
        </span>
        <span className="text-xs text-muted-foreground">
          {new Date(message.createdAt).toLocaleString("ko-KR")}
        </span>
      </div>
      {message.content ? (
        <p>{message.content}</p>
      ) : (
        <p className="italic text-muted-foreground">(빈 메시지)</p>
      )}
      {(message.model || message.feedbackRating) && (
        <div className="mt-2 flex flex-wrap items-center gap-1 pt-2 border-t border-border/60">
          {message.model && (
            <Badge variant="outline" className="text-[10px]">
              {message.model}
            </Badge>
          )}
          {message.feedbackRating && (
            <Badge variant="secondary">
              {message.feedbackRating === "up" ? "👍 추천" : "👎 비추"}
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}

export function ConversationTranscriptDrawer({
  session,
  open,
  onOpenChange,
}: Props) {
  const query = useInfiniteQuery(
    userConversationMessagesInfiniteOptions(session?.sessionId ?? null)
  );

  const messages = query.data?.pages.flatMap((page) => page.messages) ?? [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full md:max-w-[min(800px,66vw)] overflow-y-auto"
      >
        <SheetHeader>
          <SheetTitle>{session?.title || "(제목 없음)"}</SheetTitle>
          <SheetDescription>
            대화를 오래된 순으로 표시합니다. 아래에서 이어서 불러올 수 있습니다.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {session && (
            <div className="space-y-1 rounded-md border bg-muted/30 p-3 text-sm">
              <div className="flex items-center gap-2">
                <Badge
                  variant={
                    session.sessionType === "STORY" ? "default" : "secondary"
                  }
                >
                  {session.sessionType === "STORY" ? "스토리" : "일반 챗"}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  메시지 {session.messageCount.toLocaleString()}개
                </span>
              </div>
              <div className="text-xs text-muted-foreground">
                Session ID: {session.sessionId}
              </div>
              {session.workspaceName && (
                <div className="text-xs text-muted-foreground">
                  워크스페이스: {session.workspaceName}
                </div>
              )}
            </div>
          )}

          {query.isLoading && (
            <div className="space-y-3">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          )}

          {query.isError && (
            <div className="rounded border p-4 text-sm text-destructive">
              대화를 불러오지 못했습니다.
            </div>
          )}

          {query.data && (
            <>
              <div className="space-y-3">
                {messages.map((message) => (
                  <MessageBubble key={message.messageId} message={message} />
                ))}
              </div>

              {messages.length === 0 && (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  메시지가 없습니다.
                </p>
              )}

              {query.hasNextPage && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => query.fetchNextPage()}
                  disabled={query.isFetchingNextPage}
                >
                  {query.isFetchingNextPage ? "불러오는 중..." : "더 보기"}
                </Button>
              )}

              {!query.hasNextPage && messages.length > 0 && (
                <p className="text-center text-xs text-muted-foreground">
                  대화의 끝입니다.
                </p>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
